import { ReactNode } from 'react';

import { useUniqueId } from 'hooks/useUniqueId';

import { InputAffixLabel, InputContainer, InputElement } from './Input.styled';
import {
  InputAffixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.types';
import { InputProps } from './InputProps';

type Props = {
  inputProps: InputProps;
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: InputStatus;
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
  fullWidth?: boolean;
};

export function Input({
  inputProps,
  prefix,
  suffix,
  status = 'neutral',
  affixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
  fullWidth,
}: Props) {
  const inputId = useUniqueId({
    prefix: 'input-group-',
    override: inputProps.id,
  });

  return (
    <InputContainer
      $status={status}
      $affixVariant={affixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
      $fullWidth={Boolean(fullWidth)}
    >
      {prefix && <InputAffixLabel htmlFor={inputId}>{prefix}</InputAffixLabel>}

      <InputElement
        id={inputId}
        type={inputProps.type}
        name={inputProps.name}
        ref={inputProps.ref}
        title={inputProps.title}
        placeholder={inputProps.placeholder}
        value={inputProps.value}
        onChange={inputProps.onChange}
        onClick={inputProps.onClick}
        onBlur={inputProps.onBlur}
        onKeyPress={inputProps.onKeyPress}
        onKeyUp={inputProps.onKeyUp}
        onKeyDown={inputProps.onKeyDown}
        onFocus={inputProps.onFocus}
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        autoFocus={inputProps.autoFocus}
        min={inputProps.min}
        max={inputProps.max}
        step={inputProps.step}
        data-qa-id={inputProps.qaId}
        aria-label={inputProps.ariaLabel}
        aria-autocomplete={inputProps.ariaAutocomplete}
        aria-controls={inputProps.ariaControls}
        aria-activedescendant={inputProps.ariaActiveDescendant}
        aria-invalid={inputProps.ariaInvalid}
      />

      {suffix && <InputAffixLabel htmlFor={inputId}>{suffix}</InputAffixLabel>}
    </InputContainer>
  );
}
